import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowRight, Calendar, Users, Clock, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

/**
 * Dynamischer Banner zur aktuellen Masterclass.
 * - Lädt das aktive Event via /api/public/masterclass
 * - Sichtbar NUR wenn ein echtes Event existiert (event.id gesetzt) und Status nicht 'past'
 * - Wenn kein aktives Event → Komponente rendert null (Section verschwindet komplett)
 */
export default function MasterclassBanner() {
  const [event, setEvent] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/masterclass`).then((r) => {
      if (cancelled) return;
      const data = r.data || {};
      // Nur anzeigen wenn es ein echtes Event ist (event.id existiert) und Status nicht 'past'
      if (data.id && data.status !== 'past') {
        setEvent(data);
      }
      setLoaded(true);
    }).catch(() => {
      if (!cancelled) setLoaded(true);
    });
    return () => { cancelled = true; };
  }, []);

  if (!loaded || !event) return null;

  const isLive = event.status === 'live';
  const isSoldOut = event.status === 'sold_out';
  const seatsLine = isLive && event.seats_free != null
    ? `${event.seats_free} von ${event.max_seats} Plätzen frei`
    : event.max_seats
      ? `${event.max_seats} Plätze`
      : '50–100 Plätze';

  return (
    <section data-testid="masterclass-banner" className="bg-[#0a0a0a] py-20 md:py-28 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
        >
          {/* Linke Spalte — Content */}
          <div className="lg:col-span-7">
            {/* Eyebrow + Status-Badge */}
            <div className="flex items-center gap-3 mb-5">
              <span className="text-[#e07a2f] text-xs uppercase tracking-[0.3em] font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                Aktuelle Masterclass
              </span>
              {isLive && (
                <span className="inline-flex items-center gap-1.5 bg-[#befa0a]/15 border border-[#befa0a]/40 text-[#befa0a] px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-[#befa0a] animate-pulse" /> Anmeldung offen
                </span>
              )}
              {isSoldOut && (
                <span className="inline-flex items-center gap-1.5 bg-[#e07a2f]/15 border border-[#e07a2f]/40 text-[#e07a2f] px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold rounded-full" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  Ausgebucht · Warteliste
                </span>
              )}
            </div>

            <h2 className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {event.title || 'KI-Masterclass für Betreiber & Entscheider'}
            </h2>

            {event.hook_quote && (
              <p className="border-l-2 border-[#befa0a] pl-4 text-[#befa0a] text-base md:text-lg italic leading-snug mb-6 max-w-xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
                {event.hook_quote}
              </p>
            )}

            <p className="text-white/65 text-base md:text-lg leading-relaxed max-w-2xl mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {event.subtitle || 'Vom Quick Win zur sicheren Unternehmens-Infrastruktur. Eine 3-stündige Intensiv-Session — kostengünstigster Einstieg ins ENBET-Wissen.'}
            </p>

            {/* Eckdaten-Reihe */}
            <div className="flex flex-wrap gap-x-6 gap-y-3 mb-8 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
              <div className="flex items-center gap-2 text-white/80">
                <Calendar size={15} className="text-[#e07a2f]" />
                <span>{event.date_label || 'Termin wird bekanntgegeben'}{event.time_label ? ` · ${event.time_label}` : ''}</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Clock size={15} className="text-[#e07a2f]" />
                <span>{event.format_label || '3 Stunden live'}</span>
              </div>
              <div className="flex items-center gap-2 text-white/80">
                <Users size={15} className="text-[#e07a2f]" />
                <span>{seatsLine}</span>
              </div>
              {event.price_label && (
                <div className="flex items-center gap-2 text-[#befa0a] font-bold">
                  <Sparkles size={15} />
                  <span>{event.price_label}</span>
                </div>
              )}
            </div>

            <Link
              to="/ki-masterclass"
              data-testid="masterclass-banner-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}
            >
              {isSoldOut ? 'Auf die Warteliste' : 'Platz sichern'} <ArrowRight size={16} />
            </Link>
          </div>

          {/* Rechte Spalte — Visual */}
          {event.hero_image_url && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="lg:col-span-5"
            >
              <div className="relative aspect-[9/16] max-w-[320px] mx-auto overflow-hidden border-2 border-[#befa0a]/20 bg-[#0a0a0a]">
                <img src={event.hero_image_url} alt={event.title} className="w-full h-full object-contain" />
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
